/** @jsx jsx */
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconErrorSolid from '@apollo/icons/default/IconErrorSolid.svg';
import { IconButton, Tooltip } from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { useState } from 'react';

import { format } from 'src/lib/format';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { GraphRef, graphRefToString } from '../../../hooks/useGraphRef';

export const CompositionErrorsBanner = ({
  errors,
  graphRef,
  defaultOpen = false,
}: {
  errors: string[];
  graphRef: GraphRef;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(
    errors.length === 1 || defaultOpen,
  );

  return (
    <div
      className={classnames(
        'rounded border border-red-light text-red-darker',
        !isOpen ? 'bg-red-lightest hover:bg-red-lighter' : 'bg-red-lighter',
      )}
      css={css`
        /* For Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${colors.red.light};
        }

        /* For Chrome, Edge, and Safari */
        *::-webkit-scrollbar {
          width: 6px;
        }

        *::-webkit-scrollbar-track {
          background-color: transparent;
          margin-top: 10px;
          margin-bottom: 10px;
        }

        *::-webkit-scrollbar-thumb {
          background-color: ${colors.red.light};
          border-radius: 12px;
        }
      `}
    >
      <div
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <IconErrorSolid className="mr-3 text-icon-error" />
          <div className="text-sm">
            Failed to compose {graphRefToString(graphRef)} with the proposed
            subgraph.{' '}
            {isMinLengthArray(2, errors) && !defaultOpen && (
              <span>
                Expand to view all {errors.length}{' '}
                {format.pluralize(errors.length, 'error')}.
              </span>
            )}
          </div>
        </div>
        <Tooltip
          label={`${isOpen ? 'Collapse' : 'Show'} ${format.pluralize(
            errors.length,
            'error',
          )}`}
        >
          <IconButton
            aria-label={isOpen ? 'Collapse' : 'Show'}
            className="ml-4"
            variant="hidden"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            icon={isOpen ? <IconChevronUp /> : <IconChevronDown />}
          />
        </Tooltip>
      </div>
      {isOpen && (
        <div className="rounded border-t border-t-red-light bg-red-lightest px-6">
          <div
            className={classnames(
              !defaultOpen && 'max-h-28 min-h-full',
              'space-y-4 overflow-auto py-2 text-red-darker',
            )}
            // We want this element to be keyboard focusable so we need to pass the following
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
          >
            {errors.map((e) => (
              <div
                key={e}
                className="border-l-2 border-red-lighter pl-4 font-mono text-sm"
              >
                {e}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
